import { useState, useEffect, useCallback } from "react";
import DaxLiveHeader from "@/components/DaxLiveHeader";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  TrendUp,
  TrendDown,
  ChartLineUp,
  Lightning,
  Trophy,
  WarningCircle,
  ArrowUp,
  ArrowDown,
  Target,
  Percent,
  CurrencyDollar,
  ChartBar,
  X
} from "@phosphor-icons/react";

const BOT_META = {
  scalp: { label: "Scalping DAX", color: "#ef4444", icon: Lightning },
  daytrade: { label: "Day Trade", color: "#f59e0b", icon: ChartLineUp },
  swing: { label: "Swing", color: "#3b82f6", icon: TrendUp },
};

function StatCard({ label, value, sub, icon: Icon, color, trend }) {
  return (
    <div className="bg-white border border-slate-100 rounded-xl p-4 flex items-start gap-3" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}12` }}>
        <Icon size={20} weight="bold" style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-400 font-medium">{label}</p>
        <p className={`text-lg font-bold ${trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-500' : 'text-slate-800'}`}>
          {value}
        </p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function BotPerformanceCard({ botId, data }) {
  const meta = BOT_META[botId] || {};
  const Icon = meta.icon || ChartBar;

  return (
    <Card data-testid={`perf-card-${botId}`} className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${meta.color}12` }}>
            <Icon size={18} weight="bold" style={{ color: meta.color }} />
          </div>
          <div>
            <CardTitle className="text-base font-bold">{meta.label}</CardTitle>
            <p className="text-xs text-slate-400">{data.total_trades} trades total</p>
          </div>
          <div className="ml-auto text-right">
            <p className={`text-lg font-bold ${data.total_pnl >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {data.total_pnl >= 0 ? '+' : ''}{formatMoney(data.total_pnl)}
            </p>
            <p className="text-xs text-slate-400">P&L total</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="text-center p-2 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-400">Win Rate</p>
            <p className={`text-base font-bold ${data.win_rate >= 50 ? 'text-emerald-600' : 'text-red-500'}`}>
              {data.win_rate}%
            </p>
          </div>
          <div className="text-center p-2 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-400">Trades fermes</p>
            <p className="text-base font-bold text-slate-700">
              <span className="text-emerald-500">{data.wins}W</span> / <span className="text-red-400">{data.losses}L</span>
            </p>
          </div>
          <div className="text-center p-2 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-400">Drawdown max</p>
            <p className="text-base font-bold text-red-500">{formatMoney(data.max_drawdown)}</p>
          </div>
          <div className="text-center p-2 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-400">Risque/Recompense</p>
            <p className="text-base font-bold text-slate-700">{data.avg_risk_reward || '-'}R</p>
          </div>
        </div>

        {/* Best / Worst */}
        <div className="flex gap-3">
          {data.best_trade && (
            <div className="flex-1 p-2 bg-emerald-50 rounded-lg">
              <div className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                <Trophy size={12} weight="bold" /> Meilleur trade
              </div>
              <p className="text-sm font-bold text-emerald-700 mt-1">
                {data.best_trade.side?.toUpperCase()} {data.best_trade.symbol} {formatMoney(data.best_trade.pnl)}
              </p>
            </div>
          )}
          {data.worst_trade && (
            <div className="flex-1 p-2 bg-red-50 rounded-lg">
              <div className="flex items-center gap-1 text-xs text-red-600 font-medium">
                <WarningCircle size={12} weight="bold" /> Pire trade
              </div>
              <p className="text-sm font-bold text-red-700 mt-1">
                {data.worst_trade.side?.toUpperCase()} {data.worst_trade.symbol} {formatMoney(data.worst_trade.pnl)}
              </p>
            </div>
          )}
        </div>

        {/* Open Positions */}
        {data.open_positions && data.open_positions.length > 0 && (
          <div>
            <p className="text-xs text-slate-400 font-medium mb-2">Positions ouvertes ({data.open_trades})</p>
            <div className="space-y-1">
              {data.open_positions.map((pos, i) => (
                <div key={i} className="flex items-center justify-between px-2 py-1.5 bg-slate-50 rounded text-xs">
                  <div className="flex items-center gap-2">
                    {pos.side === 'long' ? <ArrowUp size={12} className="text-emerald-500" weight="bold" /> : <ArrowDown size={12} className="text-red-500" weight="bold" />}
                    <span className="font-semibold">{pos.symbol?.toUpperCase()}</span>
                    <span className="text-slate-400">{formatPrice(pos.entry_price)}</span>
                  </div>
                  <span className={`font-bold ${pos.pnl >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {pos.pnl >= 0 ? '+' : ''}{formatMoney(pos.pnl)} ({pos.pnl_pct >= 0 ? '+' : ''}{pos.pnl_pct}%)
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Equity Curve (simple bar visualization) */}
        {data.equity_curve && data.equity_curve.length > 0 && (
          <div>
            <p className="text-xs text-slate-400 font-medium mb-2">Courbe P&L</p>
            <div className="flex items-end gap-0.5 h-16">
              {data.equity_curve.map((point, i) => {
                const maxVal = Math.max(...data.equity_curve.map(p => Math.abs(p.pnl)), 1);
                const height = Math.max(4, (Math.abs(point.pnl) / maxVal) * 60);
                return (
                  <div
                    key={i}
                    className="flex-1 rounded-t transition-all"
                    style={{
                      height: `${height}px`,
                      backgroundColor: point.pnl >= 0 ? '#10b981' : '#ef4444',
                      minWidth: '3px'
                    }}
                    title={`${point.symbol}: ${formatMoney(point.pnl)}`}
                  />
                );
              })}
            </div>
          </div>
        )}

        {data.total_trades === 0 && (
          <div className="text-center py-6 text-slate-300">
            <ChartBar size={32} className="mx-auto mb-2" />
            <p className="text-sm">Aucun trade pour ce bot</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function formatMoney(val) {
  if (val === null || val === undefined) return '-';
  const abs = Math.abs(val);
  const prefix = val < 0 ? '-$' : '$';
  return `${prefix}${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPrice(p) {
  if (p === null || p === undefined) return '-';
  if (p >= 1000) return `$${p.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
  if (p >= 1) return `$${p.toFixed(4)}`;
  if (p >= 0.01) return `$${p.toFixed(6)}`;
  return `$${p.toFixed(8)}`;
}

export default function BotPerformance() {
  const [perf, setPerf] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tradeFilter, setTradeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("pnl_desc");  // "pnl_desc"|"pnl_asc"|"date_desc"|"date_asc"|"score_desc"
  const navigate = useNavigate();

  const fetchPerf = useCallback(async () => {
    try {
      const res = await fetch(`${API}/bots/performance`);
      const data = await res.json();
      setPerf(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTrades = useCallback(async () => {
    try {
      const res = await fetch(`${API}/bots/trades`);
      const data = await res.json();
      setTrades(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    fetchPerf();
    fetchTrades();
    const interval = setInterval(() => { fetchPerf(); fetchTrades(); }, 30000);
    return () => clearInterval(interval);
  }, [fetchPerf, fetchTrades]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400" data-testid="perf-loading">
        <div className="animate-spin w-8 h-8 border-2 border-slate-200 border-t-blue-500 rounded-full" />
      </div>
    );
  }

  if (!perf) return null;

  const g = perf.global || {};
  const bots = perf.bots || {};

  const closeTrade = async (tradeId) => {
    try {
      const res = await fetch(`${API}/paper/close/${tradeId}`, { method: "POST" });
      if (res.ok) {
        toast.success("Trade ferme avec succes");
        fetchPerf();
        fetchTrades();
      } else {
        toast.error("Erreur fermeture du trade");
      }
    } catch (e) {
      toast.error("Erreur fermeture du trade");
    }
  };

  const filteredTrades = trades
    .filter(t => {
      if (tradeFilter !== "all" && t.bot_id !== tradeFilter) return false;
      if (statusFilter !== "all" && t.status !== statusFilter) return false;
      return true;
    })
    .sort((a, b) => {
      // Open trades have null exit; use 0 PnL for sorting purposes
      const pnlA = a.profit_loss ?? 0;
      const pnlB = b.profit_loss ?? 0;
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      const scoreA = a.score_at_entry ?? 0;
      const scoreB = b.score_at_entry ?? 0;
      switch (sortBy) {
        case "pnl_desc":  return pnlB - pnlA;
        case "pnl_asc":   return pnlA - pnlB;
        case "date_desc": return dateB - dateA;
        case "date_asc":  return dateA - dateB;
        case "score_desc": return scoreB - scoreA;
        default: return 0;
      }
    });

  return (
    <div className="space-y-6" data-testid="bot-performance-page">
      <DaxLiveHeader />

      <div>
        <h1 className="text-2xl font-heading font-bold text-slate-900" data-testid="perf-title">
          Performance des Bots
        </h1>
        <p className="text-sm text-slate-500 mt-1">Statistiques detaillees de vos bots de trading</p>
      </div>

      {/* Global Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="global-stats">
        <StatCard
          label="P&L Total"
          value={`${g.total_pnl >= 0 ? '+' : ''}${formatMoney(g.total_pnl)}`}
          icon={CurrencyDollar}
          color={g.total_pnl >= 0 ? '#10b981' : '#ef4444'}
          trend={g.total_pnl >= 0 ? 'up' : 'down'}
        />
        <StatCard
          label="Trades Total"
          value={g.total_trades}
          icon={Target}
          color="#6366f1"
        />
        <StatCard
          label="Win Rate Global"
          value={`${g.win_rate}%`}
          icon={Percent}
          color={g.win_rate >= 50 ? '#10b981' : '#ef4444'}
          trend={g.win_rate >= 50 ? 'up' : 'down'}
        />
        <StatCard
          label="Meilleur Bot"
          value={BOT_META[g.best_bot]?.label || '-'}
          icon={Trophy}
          color="#f59e0b"
        />
      </div>

      {/* Per-Bot Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {Object.entries(bots).map(([botId, data]) => (
          <BotPerformanceCard key={botId} botId={botId} data={data} />
        ))}
      </div>

      {/* Trades Table */}
      <Card data-testid="trades-table-card">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="text-lg">Historique des Trades</CardTitle>
            <div className="flex gap-2 flex-wrap">
              <div className="flex gap-1">
                {[{id:"all",label:"Tous"},{id:"scalp",label:"Scalp"},{id:"daytrade",label:"Day Trade"},{id:"swing",label:"Swing"}].map(f => (
                  <button key={f.id} onClick={() => setTradeFilter(f.id)} data-testid={`trade-filter-${f.id}`}
                    className={`px-2.5 py-1 text-xs rounded-full transition-colors ${tradeFilter === f.id ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-1">
                {[{id:"all",label:"Tous",c:""},{id:"open",label:"Ouverts",c:"text-amber-600"},{id:"closed",label:"Fermes",c:"text-slate-600"}].map(f => (
                  <button key={f.id} onClick={() => setStatusFilter(f.id)} data-testid={`status-filter-${f.id}`}
                    className={`px-2.5 py-1 text-xs rounded-full transition-colors ${statusFilter === f.id ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {/* Sort row: best results first by default */}
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
            <span className="text-xs text-slate-400 font-medium uppercase tracking-wider mr-1">Trier:</span>
            {[
              {id:"pnl_desc",  label:"💰 Meilleurs P&L",  testid:"sort-pnl-desc"},
              {id:"pnl_asc",   label:"📉 Pires P&L",      testid:"sort-pnl-asc"},
              {id:"date_desc", label:"🕒 Plus récents",   testid:"sort-date-desc"},
              {id:"date_asc",  label:"📅 Plus anciens",   testid:"sort-date-asc"},
              {id:"score_desc",label:"⭐ Score élevé",    testid:"sort-score-desc"},
            ].map(s => (
              <button key={s.id} onClick={() => setSortBy(s.id)} data-testid={s.testid}
                className={`px-2.5 py-1 text-xs rounded-full transition-colors ${sortBy === s.id ? 'bg-emerald-600 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                {s.label}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-400 mb-3">{filteredTrades.length} trades</p>
          {filteredTrades.length === 0 ? (
            <div className="text-center py-8 text-slate-300">
              <ChartBar size={40} className="mx-auto mb-2" />
              <p className="text-sm">Aucun trade trouve</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="trades-history-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-2 px-2 text-slate-400 font-medium text-xs">Bot</th>
                    <th className="text-left py-2 px-2 text-slate-400 font-medium text-xs">Symbole</th>
                    <th className="text-left py-2 px-2 text-slate-400 font-medium text-xs">Cote</th>
                    <th className="text-right py-2 px-2 text-slate-400 font-medium text-xs">Entree</th>
                    <th className="text-right py-2 px-2 text-slate-400 font-medium text-xs">Actuel/Sortie</th>
                    <th className="text-right py-2 px-2 text-slate-400 font-medium text-xs">SL</th>
                    <th className="text-right py-2 px-2 text-slate-400 font-medium text-xs">TP</th>
                    <th className="text-right py-2 px-2 text-slate-400 font-medium text-xs">Taille</th>
                    <th className="text-right py-2 px-2 text-slate-400 font-medium text-xs">P&L</th>
                    <th className="text-center py-2 px-2 text-slate-400 font-medium text-xs">Status</th>
                    <th className="text-left py-2 px-2 text-slate-400 font-medium text-xs">Raison</th>
                    <th className="text-left py-2 px-2 text-slate-400 font-medium text-xs">Ouverture</th>
                    <th className="text-left py-2 px-2 text-slate-400 font-medium text-xs">Fermeture</th>
                    <th className="text-center py-2 px-2 text-slate-400 font-medium text-xs">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTrades.map((t) => {
                    const meta = BOT_META[t.bot_id] || {};
                    const exitOrCurrent = t.status === 'closed' ? t.exit_price : t.current_price;
                    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-';
                    const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
                    const fmtPrice = formatPrice;
                    return (
                      <tr key={t.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`trade-row-${t.id}`}>
                        <td className="py-2 px-2">
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: `${meta.color || '#94a3b8'}15`, color: meta.color || '#94a3b8' }}>
                            {meta.label || t.bot_id}
                          </span>
                        </td>
                        <td className="py-2 px-2">
                          <button
                            onClick={() => navigate(`/opportunity/${t.symbol}`)}
                            className="font-bold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                            data-testid={`symbol-link-${t.symbol}`}
                          >
                            {t.symbol}
                          </button>
                        </td>
                        <td className="py-2 px-2">
                          <span className={`flex items-center gap-1 text-xs font-bold ${t.side === 'long' ? 'text-emerald-600' : 'text-red-500'}`}>
                            {t.side === 'long' ? <ArrowUp size={12} weight="bold" /> : <ArrowDown size={12} weight="bold" />}
                            {t.side?.toUpperCase()}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-xs">{fmtPrice(t.entry_price)}</td>
                        <td className="py-2 px-2 text-right font-mono text-xs">{fmtPrice(exitOrCurrent)}</td>
                        <td className="py-2 px-2 text-right font-mono text-xs text-red-400">{fmtPrice(t.stop_loss)}</td>
                        <td className="py-2 px-2 text-right font-mono text-xs text-emerald-500">{fmtPrice(t.take_profit)}</td>
                        <td className="py-2 px-2 text-right font-mono text-xs">${t.position_value?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '-'}</td>
                        <td className="py-2 px-2 text-right">
                          <span className={`font-mono text-xs font-bold ${t.profit_loss >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {t.profit_loss >= 0 ? '+' : ''}{formatMoney(t.profit_loss)}
                          </span>
                          <span className={`block text-[10px] ${t.profit_loss_percent >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {t.profit_loss_percent >= 0 ? '+' : ''}{t.profit_loss_percent}%
                          </span>
                        </td>
                        <td className="py-2 px-2 text-center">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${t.status === 'open' ? 'bg-amber-100 text-amber-700' : t.profit_loss >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                            {t.status === 'open' ? 'Ouvert' : t.profit_loss >= 0 ? 'Gain' : 'Perte'}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-xs text-slate-500 max-w-[150px] truncate" title={t.reason}>{t.reason}</td>
                        <td className="py-2 px-2 text-xs text-slate-400 whitespace-nowrap">
                          <div>{fmtDate(t.created_at)}</div>
                          <div className="text-[10px] text-slate-300">{fmtTime(t.created_at)}</div>
                        </td>
                        <td className="py-2 px-2 text-xs text-slate-400 whitespace-nowrap">
                          {t.closed_at ? (
                            <>
                              <div>{fmtDate(t.closed_at)}</div>
                              <div className="text-[10px] text-slate-300">{fmtTime(t.closed_at)}</div>
                            </>
                          ) : <span className="text-slate-300">-</span>}
                        </td>
                        <td className="py-2 px-2 text-center">
                          {t.status === 'open' && (
                            <button
                              onClick={() => closeTrade(t.id)}
                              data-testid={`close-trade-${t.id}`}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                            >
                              <X size={12} weight="bold" /> Fermer
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
